import React, { useState } from "react";

const CCNA = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Master the fundamentals of networking, prepare for the Cisco Certified Network Associate (CCNA) certification, and gain practical skills in network configuration and troubleshooting.
          <p className="font-bold text-sm">Duration:</p> 30 days, 1 hour per day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic understanding of computer networks; familiarity with Linux/Windows systems is helpful.
          <p className="font-bold text-sm">Tools Needed:</p> A computer with Cisco Packet Tracer, GNS3 (optional), access to a physical Cisco device (if available), and internet access.
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Networking",
          objective: "Understand the basics of networking and CCNA certification.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of networking and the OSI model.</li>
                <li>Introduction to the CCNA certification (200-301).</li>
                <li>Setting up Cisco Packet Tracer.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install and explore Cisco Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Network Fundamentals – OSI and TCP/IP Models",
          objective: "Learn the foundational models of networking.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>OSI model layers and their functions.</li>
                <li>TCP/IP model and its layers.</li>
                <li>Comparing OSI and TCP/IP models.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Simulate a simple network in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Network Topologies and Devices",
          objective: "Understand network topologies and devices.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Common network topologies (star, mesh, bus).</li>
                <li>Networking devices (routers, switches, hubs).</li>
                <li>Introduction to Cisco device types.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Design a star topology in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: IP Addressing Basics",
          objective: "Learn IPv4 addressing and subnetting.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>IPv4 address structure (binary, decimal).</li>
                <li>Classes of IPv4 addresses (A, B, C).</li>
                <li>Private vs. public IP addresses.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Assign IP addresses to devices in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Subnetting – Part 1",
          objective: "Learn to subnet IP networks.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding subnets and subnet masks.</li>
                <li>Calculating subnets for a given network.</li>
                <li>Determining host and network ranges.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform subnetting for a Class C network.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Subnetting – Part 2",
          objective: "Deepen subnetting skills.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Variable Length Subnet Masking (VLSM).</li>
                <li>Subnetting for larger networks (Class A, B).</li>
                <li>Practice subnetting scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Apply VLSM to a network in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Introduction to IPv6",
          objective: "Learn IPv6 addressing basics.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>IPv6 address format and structure.</li>
                <li>IPv6 address types (unicast, multicast, anycast).</li>
                <li>Comparing IPv4 and IPv6.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure IPv6 addresses in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Cisco IOS Basics",
          objective: "Learn to navigate Cisco IOS.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Cisco IOS and CLI.</li>
                <li>Basic commands (show, configure, enable).</li>
                <li>Understanding privilege levels.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Access and explore a Cisco device CLI in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Configuring Cisco Devices",
          objective: "Learn to configure Cisco devices.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring hostnames and passwords.</li>
                <li>Setting up banners and MOTD.</li>
                <li>Saving and backing up configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a router with a hostname and MOTD.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: VLANs – Basics",
          objective: "Learn VLAN concepts and configuration.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding VLANs and their benefits.</li>
                <li>Creating and assigning VLANs to ports.</li>
                <li>Introduction to trunking.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure VLANs on a switch in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: VLANs – Advanced",
          objective: "Deepen VLAN knowledge with trunking.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configuring VLAN trunking (802.1Q).</li>
                <li>Inter-VLAN routing with a router.</li>
                <li>Troubleshooting VLAN issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure trunking and inter-VLAN routing.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Switching Concepts",
          objective: "Learn switching and STP.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Switching basics (MAC address table).</li>
                <li>Spanning Tree Protocol (STP) overview.</li>
                <li>Configuring STP on switches.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure STP and verify its operation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Routing Fundamentals",
          objective: "Learn routing concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Static vs. dynamic routing.</li>
                <li>Configuring static routes.</li>
                <li>Understanding routing tables.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure static routes between two routers.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Dynamic Routing with RIP",
          objective: "Learn RIP routing protocol.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to RIP (Routing Information Protocol).</li>
                <li>Configuring RIP on Cisco routers.</li>
                <li>Verifying RIP operation.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure RIP in a multi-router network.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Dynamic Routing with OSPF",
          objective: "Learn OSPF routing protocol.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to OSPF (Open Shortest Path First).</li>
                <li>Configuring single-area OSPF.</li>
                <li>Verifying OSPF adjacency and routing.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure OSPF in a single-area network.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: NAT and PAT",
          objective: "Learn Network Address Translation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding NAT and PAT concepts.</li>
                <li>Configuring static and dynamic NAT.</li>
                <li>Configuring PAT (Port Address Translation).</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure NAT and PAT on a router.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Access Control Lists (ACLs)",
          objective: "Learn to configure ACLs for security.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding ACL types (standard, extended).</li>
                <li>Configuring ACLs to filter traffic.</li>
                <li>Applying ACLs to interfaces.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure an extended ACL to block specific traffic.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: DHCP and DNS",
          objective: "Learn to configure DHCP and DNS services.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding DHCP and its operation.</li>
                <li>Configuring a Cisco router as a DHCP server.</li>
                <li>Basics of DNS and its role in networking.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a DHCP server on a router.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Wireless Networking Basics",
          objective: "Learn wireless networking concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to wireless LANs (WLANs).</li>
                <li>Wireless standards (802.11a/b/g/n/ac).</li>
                <li>Configuring a basic wireless network.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure a wireless access point in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Network Security Fundamentals",
          objective: "Learn basic network security practices.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Common network threats and attacks.</li>
                <li>Securing Cisco devices (passwords, SSH).</li>
                <li>Introduction to firewalls and VPNs.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enable SSH on a Cisco router.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Network Troubleshooting",
          objective: "Learn troubleshooting techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshooting methodologies.</li>
                <li>Using ping, traceroute, and show commands.</li>
                <li>Diagnosing common network issues.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Troubleshoot a network issue in Packet Tracer.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Final Project – Planning",
          objective: "Plan the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>Suggested project ideas (e.g., Small Business Network).</li>
                <li>Planning features and structure.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Final Project – Development (Part 1)",
          objective: "Develop the initial setup of the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Setting up the network topology.</li>
                <li>Configuring IP addressing and VLANs.</li>
                <li>Implementing basic routing.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Start building the project infrastructure.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Final Project – Development (Part 2)",
          objective: "Continue developing the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Adding dynamic routing (OSPF).</li>
                <li>Configuring NAT and ACLs.</li>
                <li>Testing network connectivity.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Enhance the project with advanced features.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Final Project – Testing and Debugging",
          objective: "Test and debug the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Testing network connectivity and security.</li>
                <li>Troubleshooting routing and VLAN issues.</li>
                <li>Optimizing configurations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Test and fix issues in the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Final Project – Documentation",
          objective: "Document the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating a project README.</li>
                <li>Documenting configurations and topology.</li>
                <li>Preparing a presentation outline.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write documentation for the project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Preparing for CCNA Exam – Basics",
          objective: "Review foundational networking concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review OSI model, IP addressing, and subnetting.</li>
                <li>Practice VLAN and basic routing configurations.</li>
                <li>Mock scenarios for basics.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Complete a mock basic networking task.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Preparing for CCNA Exam – Routing and Security",
          objective: "Review routing and security concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Review OSPF, NAT, and ACL configurations.</li>
                <li>Practice securing Cisco devices.</li>
                <li>Mock routing and security scenarios.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Configure OSPF and an ACL in a mock scenario.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Presentation",
          objective: "Present the final project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Delivering the project demo.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Receiving feedback from peers and instructor.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: CCNA Exam Strategy and Review",
          objective: "Prepare for the CCNA exam and review key concepts.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding the CCNA exam format (200-301).</li>
                <li>Time management and exam strategies.</li>
                <li>Review of key topics and Q&A.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Practice a timed mock exam scenario.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Understand networking fundamentals (OSI, TCP/IP, IP addressing).</li>
            <li>Configure and troubleshoot Cisco devices (routers, switches).</li>
            <li>Implement VLANs, routing protocols (RIP, OSPF), NAT, and ACLs.</li>
            <li>Secure and troubleshoot networks effectively.</li>
            <li>Prepare for the CCNA (200-301) certification exam.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Small Business Network:</span> Design and configure a network with VLANs, OSPF, NAT, and ACLs.
            </li>
            <li>
              <span className="font-bold text-sm">Campus Network:</span> Build a multi-VLAN network with inter-VLAN routing and wireless access.
            </li>
            <li>
              <span className="font-bold text-sm">Secure Branch Office:</span> Create a network with secure access, SSH, and traffic filtering using ACLs.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Practice with Cisco Packet Tracer labs and GNS3 for advanced simulations.</li>
            <li>Review the CCNA (200-301) exam topics on Cisco’s official website.</li>
            <li>Use discussion time for troubleshooting complex scenarios and Q&A.</li>
          </ul>
          <p className="text-sm">
            This syllabus covers essential CCNA topics over 30 days, preparing learners for certification and professional networking roles.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-gray-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">
          Cisco Certified Network Associate (CCNA) Syllabus
        </h1>
        <p className="text-sm text-white">(30 Hours – 1 Hour Per Day)</p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CCNA;